// frontend/src/lib/api/baseService.ts
// Base API service class with standardized request methods

import { AxiosRequestConfig } from 'axios';
import { httpClient, ApiResponse } from '../httpClient';

// Base service configuration
export interface ServiceConfig {
  baseEndpoint: string;
  timeout?: number;
}

// Request options
export interface RequestOptions {
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

// Base API service class
export abstract class BaseApiService {
  protected baseEndpoint: string;
  protected timeout: number;

  constructor(config: ServiceConfig) {
    this.baseEndpoint = config.baseEndpoint;
    this.timeout = config.timeout || 10000;
  }

  // Generic request method
  protected async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const config: AxiosRequestConfig = {
      method,
      url: `${this.baseEndpoint}${endpoint}`,
      timeout: options.timeout || this.timeout,
      headers: {
        ...options.headers,
        ...(options.skipAuth && { skipAuth: true }),
        ...(options.skipRefresh && { skipRefresh: true }),
      },
    };

    // Add query parameters
    if (options.params) {
      config.params = options.params;
    }

    // Add request body for POST/PUT/PATCH
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      config.data = data;
    }

    try {
      const response = await httpClient.request<ApiResponse<T>>(config);
      return response.data;
    } catch (error: any) {
      console.error(`API ${method} ${endpoint} failed:`, error);
      
      if (error.response?.data) {
        return error.response.data;
      }
      
      return {
        success: false,
        message: error.message || 'An error occurred',
      };
    }
  }

  // Convenience methods for common HTTP operations
  protected async get<T>(
    endpoint: string, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, undefined, options);
  }

  protected async post<T>(
    endpoint: string, 
    data?: any, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data, options);
  }

  protected async put<T>(
    endpoint: string, 
    data?: any, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data, options);
  }

  protected async patch<T>(
    endpoint: string, 
    data?: any, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  protected async delete<T>(
    endpoint: string, 
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  // File upload helper
  protected async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData: Record<string, any> = {},
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional data to FormData
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return this.post<T>(endpoint, formData, options);
  }
}

export default BaseApiService;